import { useState, useRef, useEffect, useCallback } from 'react';
import { useCamera } from '../camera/useCamera';
import { InspectionResult } from '../hooks/useSimulatedInspection';
import { drawDetectedRegions } from '../utils/canvasUtils';
import { runFullPipeline, DetectedRegion } from '../utils/imageProcessing';
import { useAddInspectionRecord } from '../hooks/useQueries';
import { getSeverityLabel, getDefectTypeLabel, getSeverityBadgeClass } from '../hooks/useQueries';
import { Severity, DefectType } from '../services/localBackend';
import {
  Camera, CameraOff, Zap, Clock, CheckCircle, AlertTriangle, XCircle,
  Loader2, RotateCcw, Activity, Sun, Moon, GitCompareArrows,
} from 'lucide-react';

type InspectionState = 'idle' | 'processing' | 'complete' | 'error';

// Map pipeline defect types to backend DefectType enum
function mapDefectType(defectType: DetectedRegion['defectType'] | null): DefectType {
  switch (defectType) {
    case 'Crack': return DefectType.crack;
    case 'Surface Wear': return DefectType.scratch;
    case 'Pitting': return DefectType.discoloration;
    case 'Tooth Damage': return DefectType.warp;
    default: return DefectType.scratch;
  }
}

// Map pipeline classification to backend Severity enum
function mapSeverity(classification: 'Good' | 'Minor Defect' | 'Major Defect'): Severity {
  switch (classification) {
    case 'Good': return Severity.good;
    case 'Minor Defect': return Severity.minorDefect;
    case 'Major Defect': return Severity.majorDefect;
  }
}

function generateComponentId(): string {
  const prefix = ['BRG', 'GER', 'BRN', 'GRN'][Math.floor(Math.random() * 4)];
  const num = Math.floor(Math.random() * 9000) + 1000;
  const suffix = ['A', 'B', 'C', 'D'][Math.floor(Math.random() * 4)];
  return `${prefix}-${num}-${suffix}`;
}

interface RealInspectionResult {
  defectType: DefectType;
  severityScore: number;
  modelConfidence: number;
  finalClassification: Severity;
  regions: DetectedRegion[];
  abnormalAreaPercentage: number;
  classification: 'Good' | 'Minor Defect' | 'Major Defect';
  componentId: string;
}

// Processing steps shown in the panel
const PROCESSING_STEPS = [
  'Converting to grayscale...',
  'Applying bilateral filter (edge-preserving)...',
  'Running Canny edge detection + NMS...',
  'Applying CLAHE contrast enhancement...',
  'Morphological close + region analysis...',
  'Computing severity with shape metrics...',
];

export default function LiveInspection() {
  const {
    isActive,
    isSupported,
    error: cameraError,
    isLoading: cameraLoading,
    startCamera,
    stopCamera,
    capturePhoto,
    videoRef,
    canvasRef: cameraCanvasRef,
  } = useCamera({ facingMode: 'user', width: 1280, height: 720, quality: 0.9, format: 'image/jpeg' });

  const resultCanvasRef = useRef<HTMLCanvasElement>(null);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [inspectionState, setInspectionState] = useState<InspectionState>('idle');
  const [inspectionResult, setInspectionResult] = useState<RealInspectionResult | null>(null);
  const [processingTime, setProcessingTime] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lightingQuality, setLightingQuality] = useState<'good' | 'fair' | 'poor' | null>(null);
  const [lightingBrightness, setLightingBrightness] = useState(0);
  const [previousResult, setPreviousResult] = useState<RealInspectionResult | null>(null);
  const [showComparison, setShowComparison] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const addInspectionRecord = useAddInspectionRecord();

  // Start camera on mount
  useEffect(() => {
    if (isSupported && !isActive && !cameraLoading) {
      startCamera();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported]);

  const handleCaptureAndInspect = useCallback(async () => {
    if (!isActive || inspectionState === 'processing') return;

    setInspectionState('processing');
    // Save current result as previous for comparison
    if (inspectionResult) {
      setPreviousResult(inspectionResult);
    }
    setInspectionResult(null);
    setSaveStatus('idle');
    setLightingQuality(null);
    setShowComparison(false);
    setElapsedTime(0);
    setCurrentStep(0);

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 100) / 10);
    }, 100);

    try {
      // Step 1: Capture photo
      setCurrentStep(0);
      const photoFile = await capturePhoto();
      if (!photoFile) throw new Error('Failed to capture photo');

      // Convert to data URL for display and processing
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(photoFile);
      });

      // Load image into an offscreen canvas for processing
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = dataUrl;
      });

      // Create offscreen canvas with captured frame
      const offscreen = document.createElement('canvas');
      offscreen.width = img.naturalWidth;
      offscreen.height = img.naturalHeight;
      const offCtx = offscreen.getContext('2d');
      if (!offCtx) throw new Error('Cannot get offscreen context');
      offCtx.drawImage(img, 0, 0);
      offscreenCanvasRef.current = offscreen;

      // Analyze lighting quality from the captured frame
      const frameData = offCtx.getImageData(0, 0, offscreen.width, offscreen.height);
      let brightnessSum = 0;
      const pixelCount = frameData.width * frameData.height;
      for (let i = 0; i < pixelCount; i++) {
        const r = frameData.data[i * 4];
        const g = frameData.data[i * 4 + 1];
        const b = frameData.data[i * 4 + 2];
        brightnessSum += (0.299 * r + 0.587 * g + 0.114 * b);
      }
      const avgBrightness = brightnessSum / pixelCount;
      setLightingBrightness(Math.round(avgBrightness));
      if (avgBrightness >= 80 && avgBrightness <= 200) {
        setLightingQuality('good');
      } else if (avgBrightness >= 50 && avgBrightness <= 220) {
        setLightingQuality('fair');
      } else {
        setLightingQuality('poor');
      }

      // Step 2-4: Run the full image processing pipeline
      setCurrentStep(1);
      await new Promise(r => setTimeout(r, 50));
      setCurrentStep(2);
      await new Promise(r => setTimeout(r, 50));
      setCurrentStep(3);
      await new Promise(r => setTimeout(r, 50));
      setCurrentStep(4);
      await new Promise(r => setTimeout(r, 50));

      // Run the actual pipeline (synchronous but fast)
      const pipelineResult = runFullPipeline(offscreen);

      setCurrentStep(5);
      await new Promise(r => setTimeout(r, 50));

      // Map to backend types
      const primaryDefect = pipelineResult.regions.length > 0
        ? pipelineResult.regions[0].defectType
        : null;
      const defectType = mapDefectType(primaryDefect);
      const finalClassification = mapSeverity(pipelineResult.classification);
      const componentId = generateComponentId();

      const result: RealInspectionResult = {
        defectType,
        severityScore: pipelineResult.severityScore,
        modelConfidence: pipelineResult.modelConfidence,
        finalClassification,
        regions: pipelineResult.regions,
        abnormalAreaPercentage: pipelineResult.abnormalAreaPercentage,
        classification: pipelineResult.classification,
        componentId,
      };

      setInspectionResult(result);

      // Draw result with bounding boxes on result canvas
      if (resultCanvasRef.current) {
        drawDetectedRegions(resultCanvasRef.current, pipelineResult.processedImageData, pipelineResult.regions);
      }

      if (timerRef.current) clearInterval(timerRef.current);
      const elapsed = (Date.now() - startTime) / 1000;
      setProcessingTime(Math.round(elapsed * 10) / 10);
      setElapsedTime(Math.round(elapsed * 10) / 10);
      setInspectionState('complete');

      // Auto-save to local backend
      setSaveStatus('saving');
      try {
        await addInspectionRecord.mutateAsync({
          componentId,
          timestamp: Date.now(),
          defectType,
          severityScore: result.severityScore,
          modelConfidence: result.modelConfidence,
          finalClassification,
          imageReference: dataUrl,
        });
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    } catch {
      if (timerRef.current) clearInterval(timerRef.current);
      setInspectionState('error');
    }
  }, [isActive, inspectionState, capturePhoto, addInspectionRecord]);

  const handleReset = () => {
    setInspectionState('idle');
    setInspectionResult(null);
    setSaveStatus('idle');
    setLightingQuality(null);
    setShowComparison(false);
    setElapsedTime(0);
    setProcessingTime(0);
    setCurrentStep(0);
  };

  const classificationIcon = inspectionResult ? (
    inspectionResult.finalClassification === Severity.good ? (
      <CheckCircle className="w-5 h-5 text-status-success" />
    ) : inspectionResult.finalClassification === Severity.minorDefect ? (
      <AlertTriangle className="w-5 h-5 text-status-warning" />
    ) : (
      <XCircle className="w-5 h-5 text-status-danger" />
    )
  ) : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">Live Inspection</h2>
          <p className="text-xs text-muted-foreground mt-0.5 font-mono">
            Real-time AI defect detection for bearings &amp; gears
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border ${isActive
            ? 'text-status-success border-status-success/30 bg-status-success/10'
            : 'text-status-danger border-status-danger/30 bg-status-danger/10'
            }`}>
            {isActive ? <Camera className="w-3 h-3" /> : <CameraOff className="w-3 h-3" />}
            {isActive ? 'Camera Connected' : 'Camera Disconnected'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Camera / Result Preview */}
        <div className="xl:col-span-3 space-y-4">
          <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card-dark">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {inspectionState === 'complete' ? 'Inspection Result' : 'Live Camera Feed'}
              </span>
              <div className="flex items-center gap-2">
                {isActive && inspectionState === 'idle' && (
                  <span className="flex items-center gap-1 text-xs text-status-success">
                    <span className="w-1.5 h-1.5 rounded-full bg-status-success blink" />
                    LIVE
                  </span>
                )}
                {inspectionState === 'processing' && (
                  <span className="flex items-center gap-1 text-xs text-status-warning">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    PROCESSING
                  </span>
                )}
              </div>
            </div>

            {/* Video/Canvas area */}
            <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
              {/* Live camera feed */}
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`w-full h-full object-cover ${inspectionState === 'complete' ? 'hidden' : 'block'
                  }`}
              />

              {/* Result canvas */}
              <canvas
                ref={resultCanvasRef}
                className={`w-full h-full object-contain ${inspectionState === 'complete' ? 'block' : 'hidden'
                  }`}
              />

              {/* Hidden camera canvas */}
              <canvas ref={cameraCanvasRef} className="hidden" />

              {/* Camera not supported */}
              {isSupported === false && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                  <div className="text-center">
                    <CameraOff className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Camera not supported in this browser</p>
                  </div>
                </div>
              )}

              {/* Camera error */}
              {cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                  <div className="text-center px-6">
                    <CameraOff className="w-12 h-12 text-status-danger mx-auto mb-3" />
                    <p className="text-sm text-status-danger font-semibold mb-1">Camera Error</p>
                    <p className="text-xs text-muted-foreground">{cameraError.message}</p>
                    <button
                      onClick={() => startCamera()}
                      className="mt-3 text-xs text-electric-blue hover:underline"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}

              {/* Loading overlay */}
              {cameraLoading && !isActive && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-electric-blue animate-spin mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">Initializing camera...</p>
                  </div>
                </div>
              )}

              {/* Processing overlay */}
              {inspectionState === 'processing' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                      <div className="absolute inset-0 border-2 border-electric-blue/30 rounded-full" />
                      <div className="absolute inset-0 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
                      <Zap className="absolute inset-0 m-auto w-6 h-6 text-electric-blue" />
                    </div>
                    <p className="text-sm font-semibold text-electric-blue mb-1">AI Analysis Running</p>
                    <p className="text-xs text-muted-foreground font-mono">{elapsedTime.toFixed(1)}s elapsed</p>
                    <p className="text-[10px] text-electric-blue/70 mt-1 font-mono">
                      {PROCESSING_STEPS[currentStep] || 'Finalizing...'}
                    </p>
                  </div>
                </div>
              )}

              {/* Scan line effect when active */}
              {isActive && inspectionState === 'idle' && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                  <div
                    className="absolute left-0 right-0 h-0.5 opacity-30"
                    style={{
                      background: 'oklch(0.72 0.18 220)',
                      animation: 'scan-line 3s linear infinite',
                    }}
                  />
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="px-4 py-3 border-t border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                {!isActive && !cameraLoading && isSupported !== false && (
                  <button
                    onClick={() => startCamera()}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-electric-blue/30 text-electric-blue hover:bg-electric-blue/10 transition-colors"
                  >
                    <Camera className="w-3 h-3" />
                    Start Camera
                  </button>
                )}
                {isActive && (
                  <button
                    onClick={() => stopCamera()}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
                  >
                    <CameraOff className="w-3 h-3" />
                    Stop
                  </button>
                )}
                {inspectionState === 'complete' && (
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    New Inspection
                  </button>
                )}
              </div>

              <button
                onClick={handleCaptureAndInspect}
                disabled={!isActive || inspectionState === 'processing'}
                className="flex items-center gap-2 px-5 py-2 rounded bg-electric-blue text-background font-bold text-sm tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed glow-blue"
              >
                {inspectionState === 'processing' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                ) : (
                  <><Zap className="w-4 h-4" /> Capture &amp; Inspect</>
                )}
              </button>
            </div>
          </div>

          {/* Processing timer */}
          {(inspectionState === 'processing' || inspectionState === 'complete') && (
            <div className="bg-card border border-border rounded-lg px-4 py-3 flex items-center gap-3">
              <Clock className="w-4 h-4 text-electric-blue flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Processing Time</span>
                  <span className="text-xs font-mono text-electric-blue">
                    {inspectionState === 'complete' ? `${processingTime}s` : `${elapsedTime.toFixed(1)}s`}
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-100"
                    style={{
                      width: `${Math.min((elapsedTime / 5) * 100, 100)}%`,
                      background: elapsedTime < 3
                        ? 'oklch(0.72 0.18 220)'
                        : elapsedTime < 4.5
                          ? 'oklch(0.75 0.18 75)'
                          : 'oklch(0.58 0.22 25)',
                    }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">Target: &lt;5 seconds</p>
              </div>
            </div>
          )}

          {/* Lighting Quality Indicator */}
          {lightingQuality && (
            <div className={`bg-card border rounded-lg px-4 py-3 flex items-center gap-3 ${lightingQuality === 'good' ? 'border-status-success/30' :
              lightingQuality === 'fair' ? 'border-status-warning/30' :
                'border-status-danger/30'
              }`}>
              {lightingQuality === 'poor' ? (
                <Moon className="w-4 h-4 text-status-danger flex-shrink-0" />
              ) : (
                <Sun className={`w-4 h-4 flex-shrink-0 ${lightingQuality === 'good' ? 'text-status-success' : 'text-status-warning'
                  }`} />
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Lighting Quality</span>
                  <span className={`text-xs font-semibold ${lightingQuality === 'good' ? 'text-status-success' :
                    lightingQuality === 'fair' ? 'text-status-warning' :
                      'text-status-danger'
                    }`}>
                    {lightingQuality === 'good' ? 'Good' : lightingQuality === 'fair' ? 'Fair' : 'Poor'}
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.min((lightingBrightness / 255) * 100, 100)}%`,
                      background: lightingQuality === 'good' ? '#22c55e' :
                        lightingQuality === 'fair' ? '#f59e0b' : '#ef4444',
                    }}
                  />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Avg brightness: {lightingBrightness}/255
                  {lightingQuality === 'poor' && ' — Consider improving lighting for better accuracy'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Results Panel */}
        <div className="xl:col-span-2 space-y-4">
          {/* Inspection Results */}
          <div className="bg-card border border-border rounded-lg shadow-card-dark">
            <div className="px-4 py-3 border-b border-border">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Inspection Results
              </span>
            </div>

            {inspectionState === 'idle' && (
              <div className="p-6 text-center">
                <Zap className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Capture an image to begin AI inspection
                </p>
                <p className="text-xs text-muted-foreground/60 mt-2">
                  Point camera at a metal surface to detect defects
                </p>
              </div>
            )}

            {inspectionState === 'processing' && (
              <div className="p-6 space-y-3">
                {PROCESSING_STEPS.map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${i < currentStep
                      ? 'bg-status-success/20 border border-status-success/50'
                      : i === currentStep
                        ? 'border border-electric-blue/50 bg-electric-blue/10'
                        : 'border border-border'
                      }`}>
                      {i < currentStep ? (
                        <CheckCircle className="w-2.5 h-2.5 text-status-success" />
                      ) : i === currentStep ? (
                        <div className="w-1.5 h-1.5 rounded-full bg-electric-blue animate-pulse" />
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/20" />
                      )}
                    </div>
                    <span className={`text-xs transition-colors duration-300 ${i < currentStep
                      ? 'text-status-success'
                      : i === currentStep
                        ? 'text-electric-blue'
                        : 'text-muted-foreground/40'
                      }`}>{step}</span>
                  </div>
                ))}
              </div>
            )}

            {inspectionState === 'complete' && inspectionResult && (
              <div className="p-4 space-y-4">
                {/* Classification badge */}
                <div className={`flex items-center gap-3 p-3 rounded border ${inspectionResult.finalClassification === Severity.good
                  ? 'bg-status-success/10 border-status-success/30'
                  : inspectionResult.finalClassification === Severity.minorDefect
                    ? 'bg-status-warning/10 border-status-warning/30'
                    : 'bg-status-danger/10 border-status-danger/30'
                  }`}>
                  {classificationIcon}
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Final Classification</p>
                    <p className={`text-base font-bold ${inspectionResult.finalClassification === Severity.good ? 'text-status-success' :
                      inspectionResult.finalClassification === Severity.minorDefect ? 'text-status-warning' :
                        'text-status-danger'
                      }`}>
                      {getSeverityLabel(inspectionResult.finalClassification)}
                    </p>
                  </div>
                </div>

                {/* Metrics grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted/30 rounded p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Defect Type</p>
                    <p className="text-sm font-semibold text-foreground">
                      {inspectionResult.regions.length > 0
                        ? inspectionResult.regions[0].defectType
                        : 'None'}
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Severity Score</p>
                    <p className={`text-sm font-semibold ${inspectionResult.finalClassification === Severity.good ? 'text-status-success' :
                      inspectionResult.finalClassification === Severity.minorDefect ? 'text-status-warning' :
                        'text-status-danger'
                      }`}>
                      {inspectionResult.severityScore} / 100
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Model Confidence</p>
                    <p className="text-sm font-semibold text-electric-blue">
                      {inspectionResult.modelConfidence}%
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Abnormal Area</p>
                    <p className={`text-sm font-semibold ${inspectionResult.abnormalAreaPercentage < 10 ? 'text-status-success' :
                      inspectionResult.abnormalAreaPercentage < 30 ? 'text-status-warning' :
                        'text-status-danger'
                      }`}>
                      {inspectionResult.abnormalAreaPercentage.toFixed(1)}%
                    </p>
                  </div>
                </div>

                {/* Detected regions list */}
                {inspectionResult.regions.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">
                      Detected Regions ({inspectionResult.regions.length})
                    </p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {inspectionResult.regions.map((region, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between bg-muted/20 rounded px-2.5 py-1.5 border border-border/50"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-status-danger flex-shrink-0" />
                            <span className="text-xs font-medium text-foreground">{region.defectType}</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground font-mono">
                            {region.boundingBox.width}×{region.boundingBox.height}px
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {inspectionResult.regions.length === 0 && (
                  <div className="flex items-center gap-2 text-status-success bg-status-success/5 rounded p-2.5 border border-status-success/20">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    <p className="text-xs">No defects detected — surface appears normal</p>
                  </div>
                )}

                {/* Component ID */}
                <div className="flex items-center justify-between pt-1 border-t border-border">
                  <span className="text-[10px] text-muted-foreground">Component ID</span>
                  <span className="text-[10px] font-mono text-muted-foreground">{inspectionResult.componentId}</span>
                </div>

                {/* Comparison toggle */}
                {previousResult && (
                  <button
                    onClick={() => setShowComparison(!showComparison)}
                    className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded border text-xs font-medium transition-all ${showComparison
                      ? 'border-electric-blue/30 bg-electric-blue/10 text-electric-blue'
                      : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                      }`}
                  >
                    <GitCompareArrows className="w-3.5 h-3.5" />
                    {showComparison ? 'Hide Comparison' : 'Compare with Previous'}
                  </button>
                )}

                {/* Save status */}
                <div className="flex items-center gap-2">
                  {saveStatus === 'saving' && (
                    <span className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" /> Saving to database...
                    </span>
                  )}
                  {saveStatus === 'saved' && (
                    <span className="flex items-center gap-1.5 text-[10px] text-status-success">
                      <CheckCircle className="w-3 h-3" /> Saved to database
                    </span>
                  )}
                  {saveStatus === 'error' && (
                    <span className="flex items-center gap-1.5 text-[10px] text-status-danger">
                      <XCircle className="w-3 h-3" /> Failed to save
                    </span>
                  )}
                </div>
              </div>
            )}

            {inspectionState === 'error' && (
              <div className="p-6 text-center">
                <XCircle className="w-10 h-10 text-status-danger mx-auto mb-3" />
                <p className="text-sm text-status-danger font-semibold mb-1">Inspection Failed</p>
                <p className="text-xs text-muted-foreground mb-3">An error occurred during processing</p>
                <button
                  onClick={handleReset}
                  className="text-xs text-electric-blue hover:underline"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>

          {/* Pipeline Info Card */}
          <div className="bg-card border border-border rounded-lg shadow-card-dark">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-electric-blue" />
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                Detection Pipeline
              </span>
            </div>
            <div className="p-4 space-y-2">
              {[
                { label: 'Bilateral Filter', desc: 'Edge-preserving noise reduction' },
                { label: 'Canny Edge Detection', desc: 'NMS + hysteresis thresholding' },
                { label: 'CLAHE Enhancement', desc: 'Adaptive contrast equalization' },
                { label: 'Morphological Close', desc: 'Gap filling + noise removal' },
                { label: 'Shape Analysis', desc: 'Circularity, solidity, Otsu threshold' },
                { label: 'Weighted Severity', desc: 'Type-weighted scoring + confidence' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded bg-electric-blue/10 border border-electric-blue/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[9px] font-bold text-electric-blue">{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-foreground">{item.label}</p>
                    <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison with Previous Inspection */}
          {showComparison && previousResult && inspectionResult && (
            <div className="bg-card border border-electric-blue/20 rounded-lg shadow-card-dark">
              <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <GitCompareArrows className="w-3.5 h-3.5 text-electric-blue" />
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Comparison with Previous
                </span>
              </div>
              <div className="p-4 space-y-3">
                {/* Classification comparison */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Previous</p>
                    <p className={`text-xs font-bold px-2 py-1 rounded inline-block ${getSeverityBadgeClass(previousResult.finalClassification)}`}>
                      {getSeverityLabel(previousResult.finalClassification)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Current</p>
                    <p className={`text-xs font-bold px-2 py-1 rounded inline-block ${getSeverityBadgeClass(inspectionResult.finalClassification)}`}>
                      {getSeverityLabel(inspectionResult.finalClassification)}
                    </p>
                  </div>
                </div>

                {/* Metrics comparison */}
                {[
                  { label: 'Severity Score', prev: previousResult.severityScore, curr: inspectionResult.severityScore, unit: '/100', lowerBetter: true },
                  { label: 'Confidence', prev: previousResult.modelConfidence, curr: inspectionResult.modelConfidence, unit: '%', lowerBetter: false },
                  { label: 'Abnormal Area', prev: Number(previousResult.abnormalAreaPercentage.toFixed(1)), curr: Number(inspectionResult.abnormalAreaPercentage.toFixed(1)), unit: '%', lowerBetter: true },
                ].map((metric) => {
                  const delta = metric.curr - metric.prev;
                  const improved = metric.lowerBetter ? delta < 0 : delta > 0;
                  return (
                    <div key={metric.label} className="flex items-center justify-between bg-muted/20 rounded px-3 py-2">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{metric.label}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-muted-foreground">{metric.prev}{metric.unit}</span>
                        <span className="text-[10px] text-muted-foreground">→</span>
                        <span className="text-xs font-semibold text-foreground">{metric.curr}{metric.unit}</span>
                        {delta !== 0 && (
                          <span className={`text-[10px] font-semibold ${improved ? 'text-status-success' : 'text-status-danger'}`}>
                            {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Defect count comparison */}
                <div className="flex items-center justify-between bg-muted/20 rounded px-3 py-2">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Defects Found</span>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-muted-foreground">{previousResult.regions.length}</span>
                    <span className="text-[10px] text-muted-foreground">→</span>
                    <span className="text-xs font-semibold text-foreground">{inspectionResult.regions.length}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
